import React, { useEffect, useRef, useState } from 'react'
import { StyleSheet, View } from 'react-native'
import { useDispatch, useSelector } from 'react-redux'
import v4 from 'uuid/v4'

import { colors } from '../../../styles/global'
import { MENTION_MODAL_GOALS_TAB } from '../../../Feeds/CommentsTextInput/textInputHelper'
import { TASK_PARENT_GOAL_MODAL_ID, removeModal, storeModal } from '../../../ModalsManager/modalsManager'
import CustomScrollView from '../../../UIControls/CustomScrollView'
import { applyPopoverWidth } from '../../../../utils/HelperFunctions'
import Backend from '../../../../utils/BackendBridge'
import MentionsGoalsGrouped from './MentionsGoalsGrouped'
import MentionProjectHeader from '../../../Feeds/CommentsTextInput/MentionsModal/MentionProjectHeader'
import EmptyMatch from '../../../Feeds/CommentsTextInput/MentionsModal/EmptyMatch'
import useWindowSize from '../../../../utils/useWindowSize'
import NewObjectsInMentions from '../../../NewObjectsInMentions/NewObjectsInMentions'
import SearchForm from '../../../GlobalSearchAlgolia/Form/SearchForm'
import {
    storeInMentionModalStack,
    removeFromMentionModalStack,
    showFloatPopup,
    hideFloatPopup,
    setSelectedGoalDataInTasksListWhenAddTask,
    toggleDismissibleActive,
} from '../../../../redux/actions'
import ActiveGoal from './ActiveGoal'
import ModalHeader from '../ModalHeader'
import { translate } from '../../../../i18n/TranslationService'
import TabsHeader, { CURRENT_MILESTONE, ALL_MILESTONES } from './TabsHeader'
import { BACKLOG_DATE_NUMERIC } from '../../../TaskListView/Utils/TasksHelper'
import { DYNAMIC_PERCENT, getOwnerId } from '../../../GoalsView/GoalsHelper'
import { ALL_GOALS_ID } from '../../../AllSections/allSectionHelper'
import useParentGoalSearch from './useParentGoalSearch'
import GhostButton from '../../../UIControls/GhostButton'
import { goalSelectionId, shouldUnselectGoal } from './goalSelectionToggle'
import { getSafeAreaModalMaxHeight } from '../../../../utils/modalSafeArea'

export default function TaskParentGoalModal({
    activeGoal,
    delalyPrivacyModalClose,
    setActiveGoal,
    notDelayClose,
    projectId,
    closeModal,
    ownerId,
    fromAddTaskSection,
    dateFormated,
}) {
    const dispatch = useDispatch()
    const loggedUserId = useSelector(state => state.loggedUser.uid)
    const projectsMap = useSelector(state => state.loggedUserProjectsMap)
    const currentUserId = useSelector(state => state.currentUser.uid)
    const smallScreenNavigation = useSelector(state => state.smallScreenNavigation)
    const selectedGoalDataFromRedux = useSelector(state => state.selectedGoalDataInTasksListWhenAddTask)
    const selectedGoalFromRedux = selectedGoalDataFromRedux?.goal

    // When in fromAddTaskSection mode, use Redux state for activeGoal
    const effectiveActiveGoal = fromAddTaskSection ? selectedGoalFromRedux : activeGoal

    // Keep the meaning of a click stable for the lifetime of this picker. A goal assigned by the
    // router after the picker opened must not turn the user's pending "select" click into
    // "unselect". The reference advances only after a choice made through this picker.
    const selectionAtLastUserChoiceRef = useRef(goalSelectionId(effectiveActiveGoal))
    const [flag, setFlag] = useState(false)
    const [activeMilestoneDate, setActiveMilestoneDate] = useState(null)
    const [currentMilestoneGoals, setCurrentMilestoneGoals] = useState([])
    const [allMilestonesGoals, setAllMilestonesGoals] = useState([])

    const [activeTab, setActiveTab] = useState(CURRENT_MILESTONE)
    const [filterText, setFilterText] = useState('')
    const { hits, loading, error, hasMore, loadMore } = useParentGoalSearch({
        projectId,
        userId: loggedUserId,
        projectsMap,
        query: filterText,
    })

    const getIfGoalsAreInCurrentMilestone = goal => {
        const { startingMilestoneDate, completionMilestoneDate, progress, dynamicProgress } = goal
        return (
            completionMilestoneDate >= activeMilestoneDate &&
            startingMilestoneDate <= activeMilestoneDate &&
            (activeMilestoneDate !== BACKLOG_DATE_NUMERIC ||
                (progress !== 100 && (progress !== DYNAMIC_PERCENT || dynamicProgress !== 100)))
        )
    }

    const items = activeTab === CURRENT_MILESTONE ? currentMilestoneGoals : allMilestonesGoals
    const currentProject = projectsMap[projectId]
    const loadedActiveGoalAndMilestones = effectiveActiveGoal && activeMilestoneDate
    const isActiveGoalInCurrentMilestoneTab =
        loadedActiveGoalAndMilestones && getIfGoalsAreInCurrentMilestone(effectiveActiveGoal)
    const isActiveGoalInAllMilestonesTab =
        loadedActiveGoalAndMilestones && !getIfGoalsAreInCurrentMilestone(effectiveActiveGoal)
    const isActiveGoalInActiveTab =
        (activeTab === CURRENT_MILESTONE && isActiveGoalInCurrentMilestoneTab) ||
        (activeTab === ALL_MILESTONES && isActiveGoalInAllMilestonesTab)

    const ADD_GOALS_FORM_INDEX = isActiveGoalInActiveTab ? -2 : -1
    const ACTIVE_GOAL_ITEM_INDEX = -1

    const activedActiveGoalTab = useRef(false)
    const modalId = useRef(null)
    const itemsRef = useRef([])
    const itemsComponentsRefs = useRef({})
    const scrollHeight = useRef(0)
    const scrollRef = useRef()
    const offsets = useRef({ top: 0, bottom: 0 })
    const newForm = useRef(null)
    const activeItemIndexRef = useRef(ADD_GOALS_FORM_INDEX)
    const searchInputRef = useRef(null)
    const activeGoalRef = useRef(null)

    const [width, height] = useWindowSize()
    const tmpHeight = getSafeAreaModalMaxHeight(height)
    const finalHeight = tmpHeight < 548 ? tmpHeight : 548

    const onKeyDown = event => {
        const { key } = event
        if (key === 'Enter') {
            event?.preventDefault?.()
            event?.stopPropagation?.()
            if (activeItemIndexRef.current === ADD_GOALS_FORM_INDEX) {
                const formIsOpen = newForm.current.isOpen()
                if (!formIsOpen) newForm.current.open()
            } else if (activeItemIndexRef.current === ACTIVE_GOAL_ITEM_INDEX) {
                selectGoal(null, activeTab, projectId, false)
            } else {
                selectGoal(itemsRef.current[activeItemIndexRef.current], activeTab, projectId, false)
            }
        } else if (key === 'ArrowDown') {
            event?.preventDefault?.()
            event?.stopPropagation?.()
            selectDown()
        } else if (key === 'ArrowUp') {
            event?.preventDefault?.()
            event?.stopPropagation?.()
            selectUp()
        } else if (key === 'Escape') {
            if (newForm.current.isOpen()) {
                event?.preventDefault?.()
                event?.stopPropagation?.()
                closeNewForm()
            }
        }
    }

    const scrollToFocusItem = currentIndex => {
        const textHeight = smallScreenNavigation ? 134 : 112
        const headerHeight = 64 + textHeight

        if (currentIndex === ADD_GOALS_FORM_INDEX) {
            if (offsets.current.top > headerHeight) scrollRef.current.scrollTo({ y: headerHeight, animated: false })
        } else if (currentIndex === ACTIVE_GOAL_ITEM_INDEX) {
            activeGoalRef.current.measure((fx, fy, width, height) => {
                if (offsets.current.top > headerHeight + fy) {
                    scrollRef.current.scrollTo({ y: headerHeight + fy + 40, animated: false })
                } else if (headerHeight + 40 + fy + height > offsets.current.bottom) {
                    const scrollArea = offsets.current.bottom - offsets.current.top
                    scrollRef.current.scrollTo({ y: headerHeight + 40 + fy + height - scrollArea, animated: false })
                }
            })
        } else {
            if (isActiveGoalInActiveTab) {
                activeGoalRef.current.measure((agFx, agFy, agWidth, agHeight) => {
                    const id = itemsRef.current[currentIndex].id
                    itemsComponentsRefs.current[id].measure((fx, fy, width, height) => {
                        if (offsets.current.top > headerHeight + agHeight + fy) {
                            scrollRef.current.scrollTo({ y: headerHeight + agHeight + fy + 40, animated: false })
                        } else if (headerHeight + agHeight + 40 + fy + height > offsets.current.bottom) {
                            const scrollArea = offsets.current.bottom - offsets.current.top
                            scrollRef.current.scrollTo({
                                y: headerHeight + agHeight + 40 + fy + height - scrollArea,
                                animated: false,
                            })
                        }
                    })
                })
            } else {
                const id = itemsRef.current[currentIndex].id
                itemsComponentsRefs.current[id].measure((fx, fy, width, height) => {
                    if (offsets.current.top > headerHeight + fy) {
                        scrollRef.current.scrollTo({ y: headerHeight + fy + 40, animated: false })
                    } else if (headerHeight + 40 + fy + height > offsets.current.bottom) {
                        const scrollArea = offsets.current.bottom - offsets.current.top
                        scrollRef.current.scrollTo({ y: headerHeight + 40 + fy + height - scrollArea, animated: false })
                    }
                })
            }
        }
    }

    const selectDown = () => {
        if (itemsRef.current.length >= 1) {
            const currentIndex = activeItemIndexRef.current
            closeNewForm()
            const nextIndex = currentIndex + 1
            activeItemIndexRef.current = nextIndex === itemsRef.current.length ? ADD_GOALS_FORM_INDEX : nextIndex
            scrollToFocusItem(activeItemIndexRef.current)
            setFlag(flag => !flag)
        }
    }

    const selectUp = () => {
        if (itemsRef.current.length >= 1) {
            const currentIndex = activeItemIndexRef.current
            closeNewForm()
            const nextIndex = currentIndex - 1
            activeItemIndexRef.current = currentIndex === ADD_GOALS_FORM_INDEX ? itemsRef.current.length - 1 : nextIndex
            scrollToFocusItem(activeItemIndexRef.current)
            setFlag(flag => !flag)
        }
    }

    const selectNewForm = () => {
        activeItemIndexRef.current = ADD_GOALS_FORM_INDEX
    }

    const closeNewForm = () => {
        if (activeItemIndexRef.current === ADD_GOALS_FORM_INDEX) {
            newForm.current.close()
        }
    }

    const onLayout = data => {
        scrollRef.current.scrollTo({ y: 0, animated: false })
        offsets.current = { top: 0, bottom: data.nativeEvent.layout.height }
        scrollHeight.current = data.nativeEvent.layout.height
    }

    const clearFilter = () => {
        setFilterText('')
    }

    const filterGoalsByCurrentMilestone = goals => {
        const currentMilestoneGoals = []
        const allMilestonesGoals = []
        goals.forEach(goal => {
            getIfGoalsAreInCurrentMilestone(goal) ? currentMilestoneGoals.push(goal) : allMilestonesGoals.push(goal)
        })

        itemsRef.current = activeTab === CURRENT_MILESTONE ? currentMilestoneGoals : allMilestonesGoals
        activeItemIndexRef.current = itemsRef.current.length === 0 ? ADD_GOALS_FORM_INDEX : 0
        setCurrentMilestoneGoals(currentMilestoneGoals)
        setAllMilestonesGoals(allMilestonesGoals)
    }

    useEffect(() => {
        closeNewForm()
        itemsRef.current = activeTab === CURRENT_MILESTONE ? currentMilestoneGoals : allMilestonesGoals
        activeItemIndexRef.current = itemsRef.current.length === 0 ? ADD_GOALS_FORM_INDEX : 0
        setFlag(flag => !flag)
    }, [activeTab])

    const dismissClickThroughEditModes = () => {
        // Dismiss any task edit modes that may have been triggered by click-through
        // Use setTimeout to ensure this runs after any accidental edit mode activation
        setTimeout(() => {
            dispatch(toggleDismissibleActive(false))
        }, 50)
    }

    const selectGoal = async (goal, tabIndex, goalProjectId, isNewGoal) => {
        dismissClickThroughEditModes()

        // A repeated click is an unselect only if the goal was already selected when the picker
        // opened (or when the user last chose through it). Live background updates may change what
        // is rendered, but must not invert the interaction already in flight.
        const currentlySelectedGoal = effectiveActiveGoal || activeGoal
        if (shouldUnselectGoal(goal, currentlySelectedGoal, selectionAtLastUserChoiceRef.current)) {
            unselectGoal()
            return
        }
        selectionAtLastUserChoiceRef.current = goalSelectionId(goal)

        // Use the goal's projectId if available, otherwise fall back to the passed goalProjectId
        const targetProjectId = goal?.projectId || goalProjectId

        if (fromAddTaskSection) {
            const goalData = { projectId: targetProjectId, goal, dateFormated, isNewGoal }
            dispatch(setSelectedGoalDataInTasksListWhenAddTask(goalData))
            closeModal()
        } else {
            // Persist the relationship before dismissing this picker. The detailed-view property
            // writes asynchronously; closing first made the interaction look successful while the
            // task was still unlinked (and hid write failures entirely).
            const saved = await setActiveGoal(goal, targetProjectId)
            if (saved === false) return

            if (notDelayClose) closeModal()
            else setTimeout(closeModal)
        }
    }

    const unselectGoal = async () => {
        dismissClickThroughEditModes()
        selectionAtLastUserChoiceRef.current = null

        if (fromAddTaskSection) {
            dispatch(setSelectedGoalDataInTasksListWhenAddTask({ projectId, goal: null, dateFormated }))
        } else {
            const saved = await setActiveGoal(null)
            if (saved === false) return
        }
        closeModal()
    }

    const updateActiveMilestone = milestone => {
        setActiveMilestoneDate(milestone ? milestone.date : BACKLOG_DATE_NUMERIC)
    }

    useEffect(() => {
        if (!activedActiveGoalTab.current && activeMilestoneDate && effectiveActiveGoal) {
            if (activeTab === CURRENT_MILESTONE) {
                if (isActiveGoalInAllMilestonesTab) setActiveTab(ALL_MILESTONES)
            } else {
                if (isActiveGoalInCurrentMilestoneTab) setActiveTab(CURRENT_MILESTONE)
            }
            activedActiveGoalTab.current = true
        }
    }, [activeMilestoneDate, effectiveActiveGoal])

    useEffect(() => {
        if (activeMilestoneDate) {
            filterGoalsByCurrentMilestone(
                hits.filter(goal => goalSelectionId(goal) !== goalSelectionId(effectiveActiveGoal))
            )
        }
    }, [activeMilestoneDate, hits, effectiveActiveGoal])

    useEffect(() => {
        const watcherKey = v4()
        const ownerUserId = getOwnerId(
            projectId,
            ownerId ? ownerId : currentUserId === ALL_GOALS_ID ? loggedUserId : currentUserId
        )
        Backend.watchActiveMilestone(projectId, watcherKey, updateActiveMilestone, ownerUserId)
        return () => {
            Backend.unwatch(watcherKey)
        }
    }, [])

    useEffect(() => {
        storeModal(TASK_PARENT_GOAL_MODAL_ID)
        return () => {
            removeModal(TASK_PARENT_GOAL_MODAL_ID)
        }
    }, [])

    useEffect(() => {
        const mentionModalId = Backend.getId()
        modalId.current = mentionModalId
        dispatch([showFloatPopup(), storeInMentionModalStack(mentionModalId)])
        return () => {
            dispatch([hideFloatPopup(), removeFromMentionModalStack(mentionModalId)])
        }
    }, [])

    useEffect(() => {
        document.addEventListener('keydown', onKeyDown, true)
        return () => {
            document.removeEventListener('keydown', onKeyDown, true)
        }
    })

    const stopPropagation = e => {
        e?.stopPropagation?.()
    }

    const claimResponder = () => true

    return (
        <View
            style={[localStyles.container, applyPopoverWidth(), { maxHeight: finalHeight }]}
            onClick={stopPropagation}
            onMouseDown={stopPropagation}
            onTouchStart={stopPropagation}
            onTouchEnd={stopPropagation}
            onStartShouldSetResponder={claimResponder}
            onMoveShouldSetResponder={claimResponder}
            onResponderTerminationRequest={() => false}
        >
            <CustomScrollView
                ref={scrollRef}
                showsVerticalScrollIndicator={false}
                scrollOnLayout={onLayout}
                onScroll={({ nativeEvent }) => {
                    const y = nativeEvent.contentOffset.y
                    offsets.current = { top: y, bottom: y + scrollHeight.current }
                }}
                indicatorStyle={{ right: -6 }}
            >
                <ModalHeader
                    closeModal={closeModal}
                    title={translate('Choose the goal to link this task')}
                    description={translate('Choose the goal to link this task description')}
                    containerStyle={localStyles.header}
                    disabledEscape={newForm.current?.isOpen()}
                />

                <SearchForm
                    searchInputRef={searchInputRef}
                    onPressButton={clearFilter}
                    localText={filterText}
                    setLocalText={setFilterText}
                    containerStyle={localStyles.textFilter}
                    placeholder={`${translate('Filter')}...`}
                    buttonIcon="x"
                />
                <View style={{ paddingHorizontal: 8 }}>
                    <TabsHeader
                        setActiveTab={setActiveTab}
                        activeTab={activeTab}
                        currentMilestoneGoalsAmount={
                            isActiveGoalInCurrentMilestoneTab
                                ? currentMilestoneGoals.length + 1
                                : currentMilestoneGoals.length
                        }
                        allMilestonesGoalsAmount={
                            isActiveGoalInAllMilestonesTab ? allMilestonesGoals.length + 1 : allMilestonesGoals.length
                        }
                    />
                </View>
                {currentProject && (
                    <MentionProjectHeader
                        project={currentProject}
                        amount={
                            items.filter(goal => goal.projectId === projectId).length +
                            (isActiveGoalInActiveTab ? 1 : 0)
                        }
                    />
                )}
                <NewObjectsInMentions
                    ref={newForm}
                    projectId={projectId}
                    selectItemToMention={selectGoal}
                    activeTab={MENTION_MODAL_GOALS_TAB}
                    hover={activeItemIndexRef.current === ADD_GOALS_FORM_INDEX}
                    selectNewForm={selectNewForm}
                    modalId={modalId.current}
                    mentionText={filterText}
                    delalyPrivacyModalClose={delalyPrivacyModalClose}
                />
                {isActiveGoalInActiveTab && (
                    <ActiveGoal
                        unselectGoal={unselectGoal}
                        projectId={projectId}
                        activeGoal={effectiveActiveGoal}
                        activeGoalRef={activeGoalRef}
                        hover={activeItemIndexRef.current === ACTIVE_GOAL_ITEM_INDEX}
                    />
                )}
                {items.length > 0 ? (
                    <MentionsGoalsGrouped
                        selectItemToMention={selectGoal}
                        goals={items}
                        activeItemIndex={activeItemIndexRef.current}
                        itemsComponentsRefs={itemsComponentsRefs}
                        currentProjectId={projectId}
                        currentlyAssignedGoal={effectiveActiveGoal}
                        showCurrentProjectHeader={false}
                    />
                ) : (
                    <EmptyMatch
                        sppinerContainerStyle={localStyles.sppinerContainer}
                        showSpinner={loading || (!activeMilestoneDate && !error)}
                        text={translate(error ? 'Search is temporarily unavailable' : 'There are not results to show')}
                    />
                )}
                {error && items.length > 0 && <EmptyMatch text={translate('Search is temporarily unavailable')} />}
                {(hasMore || error) && (
                    <GhostButton
                        title={translate(loading ? 'Loading' : error ? 'Try again' : 'Load more')}
                        onPress={loadMore}
                        disabled={loading}
                        type="ghost"
                    />
                )}
            </CustomScrollView>
        </View>
    )
}

const localStyles = StyleSheet.create({
    container: {
        backgroundColor: colors.Secondary400,
        borderRadius: 4,
        boxShadow: '0px 4px 16px rgba(78,93,120,0.56)',
        elevation: 3,
        paddingHorizontal: 8,
        paddingTop: 8,
        paddingBottom: 16,
        maxHeight: 424,
    },
    header: {
        marginHorizontal: 8,
        marginTop: 8,
    },
    sppinerContainer: {
        paddingBottom: 36.5,
    },
    textFilter: {
        paddingHorizontal: 8,
    },
})
